import { useEffect, useRef } from 'react';
import { MeetingStatus } from 'amazon-chime-sdk-component-library-react';
import { toast } from 'react-toastify';
import i18n from '../../../i18n/i18n';

const useAudioInputAutoSelect = ({ meetingStatus, meetingManager, initialAudioInputDeviceId }) => {
  const knownInputIdsRef = useRef(new Set());
  const activeInputIdRef = useRef(null);
  const lastToastAtRef = useRef(0);
  const initialSyncDoneRef = useRef(false);
  const hasSeededKnownInputsRef = useRef(false);

  useEffect(() => {
    if (meetingStatus !== MeetingStatus.Succeeded) {
      return () => {};
    }

    let cancelled = false;
    let applying = false;
    let pending = false;
    let deviceChangeTimer = null;
    let suppressNotFoundErrorUntil = 0;

    const syncAndAutoSelectInput = async () => {
      if (cancelled || applying) {
        pending = true;
        return;
      }

      applying = true;
      try {
        const av = meetingManager.meetingSession?.audioVideo;
        if (!av) {
          return;
        }

        const devices = await av.listAudioInputDevices();
        if (!devices?.length || cancelled) {
          return;
        }

        const currentIds = new Set(devices.map((device) => device.deviceId));
        const previousIds = hasSeededKnownInputsRef.current
          ? knownInputIdsRef.current
          : currentIds;
        if (!hasSeededKnownInputsRef.current) {
          knownInputIdsRef.current = currentIds;
          hasSeededKnownInputsRef.current = true;
        }
        const addedDevices = devices.filter((device) => !previousIds.has(device.deviceId));

        const currentActive = activeInputIdRef.current;
        const hasCurrentActive = !!(currentActive && currentIds.has(currentActive));
        const hasInitial = !!(initialAudioInputDeviceId && currentIds.has(initialAudioInputDeviceId));

        const nextDeviceId = addedDevices.length
          ? addedDevices[addedDevices.length - 1].deviceId
          : (hasCurrentActive ? currentActive : (hasInitial ? initialAudioInputDeviceId : devices[0].deviceId));

        const nextDevice = devices.find((device) => device.deviceId === nextDeviceId);

        knownInputIdsRef.current = currentIds;

        if (!nextDeviceId || nextDeviceId === currentActive) {
          return;
        }

        if (typeof meetingManager.startAudioInputDevice === 'function') {
          await meetingManager.startAudioInputDevice(nextDeviceId);
        } else if (typeof av.chooseAudioInputDevice === 'function') {
          await av.chooseAudioInputDevice(nextDeviceId);
        }

        activeInputIdRef.current = nextDeviceId;

        if (addedDevices.length > 0 && initialSyncDoneRef.current) {
          const nowMs = Date.now();
          if (nowMs - lastToastAtRef.current > 2500) {
            lastToastAtRef.current = nowMs;
            const label = nextDevice?.label || i18n.t('VideoChatV2.audio.newMicrophone');
            toast.info(i18n.t('VideoChatV2.audio.microphoneSwitchedTo', { label }), {
              autoClose: 2500,
              position: 'bottom-right',
            });
          }
        }
      } catch (error) {
        const message = `${error?.message || ''}`.toLowerCase();
        const isNotFound = message.includes('requested device not found') || message.includes('not found');
        const now = Date.now();

        if (isNotFound) {
          suppressNotFoundErrorUntil = now + 2000;
          pending = true;
        } else if (now > suppressNotFoundErrorUntil) {
          // Non-fatal.
          // eslint-disable-next-line no-console
          console.warn('[AudioInputAutoSelect] Failed to switch input device.', error);
        }
      } finally {
        applying = false;
        if (pending && !cancelled) {
          pending = false;
          syncAndAutoSelectInput();
        }
        initialSyncDoneRef.current = true;
      }
    };

    syncAndAutoSelectInput();

    const handleDeviceChange = () => {
      if (deviceChangeTimer) {
        clearTimeout(deviceChangeTimer);
      }
      deviceChangeTimer = setTimeout(() => {
        deviceChangeTimer = null;
        syncAndAutoSelectInput();
      }, 180);
    };

    if (typeof navigator !== 'undefined' && navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    }

    return () => {
      cancelled = true;
      if (deviceChangeTimer) {
        clearTimeout(deviceChangeTimer);
        deviceChangeTimer = null;
      }
      if (typeof navigator !== 'undefined' && navigator.mediaDevices?.removeEventListener) {
        navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      }
    };
  }, [meetingStatus, meetingManager, initialAudioInputDeviceId]);
};

export default useAudioInputAutoSelect;
